import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { filterFetch } from "@/redux/filter.actions"
import { surveyFetch } from "@/redux/survey.actions"
import { fetchFilters } from "@/redux/_archive.actions"
import { DashboardFilters } from "@/redux/filter.types"
import Dropdown from "@/components/dropdown"
import Modal from "@/components/modal"
import PageLoader from "@/components/page-loader"
import Button from "@/components/button"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVING } from "@/redux/_status.types"

interface Props {
  selectedNames: string[]
  onClose: () => void
  onSave: (names: string[]) => Promise<boolean>
}

export default function RoleScopeModal({ selectedNames, onClose, onSave }: Props) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const color = useAppSelector((state) => state._session.accountColors.active)
  const [selected, setSelected] = useState(selectedNames)
  const [names, setNames] = useState<string[]>([])
  const [status, setStatus] = useState("loading")
  const [attempt, setAttempt] = useState(0)
  const [saving, setSaving] = useState(false)
  const [surveys, setSurveys] = useState<{ id: string; name: string }[]>([])
  const [surveyId, setSurveyId] = useState("")
  const [surveysStatus, setSurveysStatus] = useState("loading")
  const sources = [{ id: "", name: t("filters_custom") }, ...surveys]

  useEffect(() => {
    let cancelled = false
    setSurveysStatus("loading")
    async function loadSurveys() {
      const response: any = await dispatch(surveyFetch())
      if (cancelled) return
      if (!Array.isArray(response)) setSurveysStatus("error")
      else {
        setSurveys(response.map(({ id, name }) => ({ id, name })))
        setSurveysStatus("loaded")
      }
    }
    loadSurveys()
    return () => { cancelled = true }
  }, [dispatch, attempt])

  useEffect(() => {
    let cancelled = false
    setStatus("loading")
    async function load() {
      const response: any = await dispatch(surveyId
        ? fetchFilters(new DashboardFilters(), surveyId)
        : filterFetch(false, false))
      if (cancelled) return
      if (response.error || !Array.isArray(response)) setStatus("error")
      else {
        setNames(Array.from(new Set<string>(response.map((filter) => filter.name))))
        setStatus("loaded")
      }
    }
    load()
    return () => { cancelled = true }
  }, [dispatch, surveyId, attempt])

  async function save() {
    if (saving || status !== "loaded") return
    setSaving(true)
    try {
      if (await onSave(selected)) onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={t("roles_dashboard_scope")} onClose={() => { if (!saving) onClose() }}
      isLarge
      // Le menu de la dropdown deborde de la zone de contenu : sans ca, la
      // modale affiche une barre de defilement horizontale et verticale.
      disableOverflow
      isCloseButtonVisible
      onNext={save} onNextCta={t("utils_save")} nextDisabled={saving || status !== "loaded"}
      status={saving ? STATUS_SAVING : undefined}>
      {/** La source des filtres se choisit a droite du texte d'explication. */}
      <div className="flex gap-4 items-start mb-4">
        <div className="flex1">
          <p>{t("roles_dashboard_scope_select")}</p>
          <p className="grey-t">{t("roles_dashboard_scope_example")}</p>
        </div>

        <div className="flex-none">
          <Dropdown
            title={t("filters")}
            displayField="name"
            active={surveyId}
            value={sources.find((source) => source.id === surveyId)?.name ?? ""}
            list={sources}
            isDisabled={saving || surveysStatus === "loading"}
            onSelect={(source: { id: string }) => {
              if (source.id !== surveyId) {
                setStatus("loading")
                setSurveyId(source.id)
              }
            }}
          />
          {surveysStatus === "error" && (
            <Button onClick={() => setAttempt((value) => value + 1)}>{t("roles_retry")}</Button>
          )}
        </div>
      </div>

      {/** Hauteur minimale : la modale ne saute pas quand on change de source
          ou quand le loader remplace les filtres. */}
      <div style={{ minHeight: 120 }}>
        <PageLoader status={status === "loading" ? STATUS_LOADING : STATUS_LOADED}>
          {status === "error" ? (
            <Button onClick={() => setAttempt((value) => value + 1)}>{t("roles_retry")}</Button>
          ) : (
            <div className="flex flex-wrap">
              {/** Meme rendu que les filtres du tableau de bord, actif ou non. */}
              {Array.from(new Set([...selected, ...names])).map((name) => {
                const isActive = selected.includes(name)
                return (
                  <div
                    key={name}
                    className={"filters-item flex" + (isActive ? " filters-item-active" : " filters-item-activable")}
                    style={{ color: isActive ? color : "" }}
                    onClick={() => { if (!saving) setSelected((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name]) }}>
                    {name}
                  </div>
                )
              })}
            </div>
          )}
        </PageLoader>
      </div>

    </Modal>
  )
}
